import React, { useState, forwardRef, useImperativeHandle } from 'react';
import {
  Flex,
  Box,
  Table,
  Tbody,
  Text,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import {
  createColumnHelper,
} from '@tanstack/react-table';
import PageTable from 'components/layout/PageTable';
import AdminTableCell from 'components/layout/AdminTableCell';
import SortableTableHeader from 'components/layout/SortableTableHeader';
import { useSortableAdminTable } from 'hooks/useSortableAdminTable';
import { getSortableColumnOptions } from 'utils/tableColumnOptions';
import TablePagination from 'components/layout/TablePagination';
import ServiceListEditModal from './ServiceListEditModal';
import { deleteData, putData } from 'utils/httpClient';
import authService from 'services/authService';
import { formatServiceDateDisplay } from '../../utils/dateOnly';
import ConfirmModal from '../modal/ConfirmModal';
import {
  isSimplifiedWorkshopView,
  isWarrantyServiceType,
} from '../../utils/warrantyTypes';
import { getVehicleBrand } from '../../utils/vehicleBrand';

const columnHelper = createColumnHelper();

const ServiceListTable = forwardRef(function ServiceListTable({
  columnsData,
  data,
  setData,
  page,
  setPage,
  totalPages,
  pageSize,
  setPageSize,
  onValidateCampaigns,
  sorting,
  onSortingChange,
}, ref) {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const columns = React.useMemo(
    () => createColumns(columnsData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnsData],
  );

  const [editModalState, setEditModalState] = React.useState({ isOpen: false });
  const [selectedVehicleService, setSelectedVehicleService] =
    React.useState(null);
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const toast = useToast();

  const confirmDeleteService = async () => {
    if (!serviceToDelete) return;
    setIsDeleting(true);
    try {
      await deleteData(`/vehicleServices/${serviceToDelete.id}`);
      setData((prev) => prev.filter((item) => item.id !== serviceToDelete.id));
      setServiceToDelete(null);
      toast({
        title: 'Servicio eliminado',
        description: 'El servicio cancelado y su cita fueron eliminados.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'No se pudo eliminar',
        description: error?.message || 'Intente nuevamente.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Exposer funciones al componente padre a través de ref
  useImperativeHandle(ref, () => ({
    openEditModalDirectly: () => {
      // Usa el servicio ya seleccionado en selectedVehicleService
      if (selectedVehicleService) {
        setEditModalState({ isOpen: true });
      }
    }
  }));

  const table = useSortableAdminTable({
    data,
    columns,
    sorting,
    onSortingChange,
    getRowId: (row) => String(row.id),
  });

  const openEditModal = async (rowData) => {
    // Validar campañas del vehículo antes de abrir el modal de edición
    const warrantyService = isWarrantyServiceType(
      rowData.serviceType || rowData.serviceTypeId,
    );
    if (
      onValidateCampaigns &&
      !warrantyService &&
      rowData.vehicle?.chassisNumber
    ) {
      // Guardar el servicio antes de validar campañas
      setSelectedVehicleService(rowData);
      
      const hasPendingCampaigns = await onValidateCampaigns(rowData.vehicle.chassisNumber);
      
      // Si hay campañas pendientes, el modal de edición se abrirá después de cerrar el modal de campañas
      if (hasPendingCampaigns) {
        return;
      }
    }
    
    // Si no hay campañas pendientes, abrir directamente el modal de edición
    setSelectedVehicleService(rowData);
    setEditModalState({ isOpen: true });
  };

  const handleAttemptClose = () => {
    setIsConfirmCloseOpen(true); // Abre el modal de confirmación
  };

  const confirmCloseEditModal = () => {
    setEditModalState({ isOpen: false });
    setSelectedVehicleService(null);
    setIsConfirmCloseOpen(false); // Cerrar el confirm modal
  };

  const cancelCloseEditModal = () => {
    setIsConfirmCloseOpen(false); // Solo cerrar confirm modal
  };

  const putVehicleService = async (service) => {
    try {
      const response = await putData(`/vehicleServices/${service.id}`, service);
      setSelectedVehicleService(response.payload.vehicleService);
      setData((prevData) =>
        prevData.map((item) =>
          item.id === service.id ? response.payload.vehicleService : item,
        ),
      );
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el servicio.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const onSave = async (updatedFields) => {
    if (!selectedVehicleService) return;

    const isSimplifiedView = isSimplifiedWorkshopView(
      updatedFields.serviceTypeId || selectedVehicleService.serviceTypeId,
      [],
      {
        warrantyType: selectedVehicleService?.warrantyType,
        description: selectedVehicleService?.description,
        serviceName: selectedVehicleService?.serviceName,
        agenda: selectedVehicleService?.agenda,
        service: selectedVehicleService?.service,
      },
    );

    // Preparar lista de productos usados (marcados como usados)
    const usedProducts = isSimplifiedView
      ? []
      : [
          ...(selectedVehicleService.service?.recommendedProducts || [])
            .filter(
              (rp) => updatedFields.recommendedProductsChecked[rp.productId],
            )
            .map((rp) => ({
              productId: rp.productId,
              quantity: rp.quantity,
              productName: rp.product?.name,
              price: rp.product?.currentCost,
              currencyId: rp.product?.currencyId,
            })),
          ...(updatedFields.addedProducts || []).map((product) => ({
            productId: product.productId,
            quantity: product.quantity,
            productName: product.name,
            price: product.price,
            currencyId: product.currencyId,
          })),
        ];

    // Preparar lista completa del servicio actualizado
    const updatedService = {
      ...selectedVehicleService,
      vehicleKilometers: updatedFields.vehicleKilometers,
      statusId: updatedFields.statusId,
      serviceTypeId: updatedFields.serviceTypeId
        ? Number(updatedFields.serviceTypeId)
        : selectedVehicleService.serviceTypeId,
      customerObservations: updatedFields.customerObservations,
      mechanicObservation: updatedFields.mechanicObservation,
      canceledObservation: updatedFields.canceledObservation,
      date: updatedFields.date,
      time: updatedFields.time,
      mechanicId: Number(updatedFields.mechanicId),
      assistantId: updatedFields.assistantId
        ? Number(updatedFields.assistantId)
        : null,

      vehicleServiceEventsHistories: isSimplifiedView
        ? []
        : (selectedVehicleService.vehicleServiceEventsHistories || []).map(
            (ev) => ({
              ...ev,
              completed: updatedFields.eventsChecked[ev.id]?.completed || false,
              comment: updatedFields.eventsChecked[ev.id]?.comment || '',
            }),
          ),

      usedProducts: usedProducts,
    };

    await putVehicleService(updatedService);
     toast({
        title: 'Servicio actualizado',
        description: 'El servicio se ha actualizado correctamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
  };

  function createColumns(columnsData) {
    return columnsData.map((col) =>
      columnHelper.accessor(col.accessor, {
        ...getSortableColumnOptions(col),
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }}>{col.Header}</Text>
        ),
        cell: (info) => {
          const value = info.getValue();

          // Formato personalizado para campos de fecha (agenda o service.date)
          if (col.accessor === 'date') {
            const formattedDate = formatServiceDateDisplay(info.row.original);

            return (
              <Text fontSize="sm" fontWeight="normal">
                {formattedDate}
              </Text>
            );
          }

          // Formato personalizado para campos de hora
          if (col.accessor === 'time') {
            const agendaTime = info.row.original.agenda?.find(
              (appointment) => appointment?.time,
            )?.time;
            const raw = agendaTime || value;
            if (!raw) {
              return (
                <Text fontSize="sm" fontWeight="normal">
                  —
                </Text>
              );
            }
            const time = new Date(`1970-01-01T${raw}`);
            const formattedTime = time.toLocaleTimeString('es-UY', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });
            return (
              <Text fontSize="sm" fontWeight="normal">
                {formattedTime}
              </Text>
            );
          }

          if (col.accessor === 'vehicle.brand') {
            const brand = getVehicleBrand(info.row.original?.vehicle);
            return (
              <Text fontSize="sm" fontWeight="normal">
                {brand || 'N/A'}
              </Text>
            );
          }

          if (col.accessor === 'vehicle.model.description') {
            const vehicle = info.row.original?.vehicle;
            const modelLabel =
              vehicle?.model?.description ||
              (typeof vehicle?.model === 'string' ? vehicle.model : null) ||
              'N/A';
            return (
              <Text fontSize="sm" fontWeight="normal">
                {modelLabel}
              </Text>
            );
          }

          // Manejo especial para propiedades anidadas que pueden ser null
          if (col.accessor.includes('.')) {
            const keys = col.accessor.split('.');

            let nestedValue = info.row.original;
            
            for (const key of keys) {
              nestedValue = nestedValue?.[key];
              if (nestedValue === null || nestedValue === undefined) {
                break;
              }
            }
            
            // Para el estado específicamente, mostrar un mensaje más descriptivo
            if (col.accessor === 'status.name' && (!nestedValue || nestedValue === null)) {
              const agendaStatus = info.row.original.agenda?.find(
                (appointment) => appointment?.status,
              )?.status;
              return (
                <Text fontSize="sm" fontWeight="normal">
                  {agendaStatus || 'Pendiente'}
                </Text>
              );
            }

            const row = info.row.original;
            const repairText = [
              row?.description,
              row?.serviceName,
              ...(Array.isArray(row?.agenda) ? row.agenda.map((item) => item?.description) : []),
            ]
              .join(' ')
              .toLowerCase();
            const isWarrantyRepair = repairText.includes('garant');
            if (
              isWarrantyRepair &&
              (col.accessor === 'serviceType.name' || col.accessor === 'service.name')
            ) {
              return (
                <Text fontSize="sm" fontWeight="normal">
                  Garantía
                </Text>
              );
            }

            return (
              <Text fontSize="sm" fontWeight="normal">
                {nestedValue || 'N/A'}
              </Text>
            );
          }

          // Acciones (como editar)
          if (col.actions) {
            const row = info.row.original;
            const statusName =
              row.status?.name ||
              row.agenda?.find((appointment) => appointment?.status)?.status ||
              '';
            const canDelete =
              authService.isAdmin() && statusName.toLowerCase() === 'cancelado';
            const visibleActions = col.actions.filter(
              (action) => action.type !== 'delete' || canDelete,
            );
            return (
              <Flex align="center">
                {visibleActions.map((action, index) => (
                  <IconButton
                    key={index}
                    aria-label={action.label}
                    icon={action.icon}
                    size="sm"
                    variant="ghost"
                    colorScheme={action.colorScheme}
                    ml={2}
                    onClick={() => {
                      if (action.type === 'edit') {
                        openEditModal(row);
                      } else if (action.type === 'delete') {
                        setServiceToDelete(row);
                      }
                    }}
                  />
                ))}
              </Flex>
            );
          }

          // Valor por defecto
          return (
            <Text fontSize="sm" fontWeight="normal">
              {value || 'N/A'}
            </Text>
          );
        },
      }),
    );
  }

  return (
    <>
      <PageTable
        flexDirection="column"
        w="100%"
        px="0px"
       >
        <Box>
          <PageTable.Scroll>
            <Table variant="simple" color="gray.700" mb="24px" mt="12px">
                        <Thead>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                              {headerGroup.headers.map((header) => (
                                <SortableTableHeader
                                  key={header.id}
                                  header={header}
                                  borderColor={borderColor}
                                />
                              ))}
                            </Tr>
                          ))}
                        </Thead>
                        <Tbody>
                          {table.getRowModel().rows.map((row) => (
                            <Tr key={row.id}>
                              {row.getVisibleCells().map((cell) => (
                                <AdminTableCell key={cell.id} cell={cell} />
                              ))}
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
          </PageTable.Scroll>
          <TablePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </Box>
      </PageTable>
      <ServiceListEditModal
        modalState={editModalState}
        closeModal={handleAttemptClose}
        selectedService={selectedVehicleService}
        onSave={onSave}
      />
      <ConfirmModal
        isOpen={isConfirmCloseOpen}
        onClose={cancelCloseEditModal}
        onConfirm={confirmCloseEditModal}
        header="Cerrar sin guardar"
        message="¿Estás seguro de que querés cerrar el editor? Se perderán los cambios no guardados."
      />
      <ConfirmModal
        isOpen={!!serviceToDelete}
        onClose={() => !isDeleting && setServiceToDelete(null)}
        onConfirm={confirmDeleteService}
        header="Eliminar servicio"
        message="Esta acción eliminará definitivamente el servicio cancelado y su cita. El stock consumido ya fue restaurado al cancelar."
        confirmLabel="Eliminar"
        confirmVariant="danger"
        isLoading={isDeleting}
      />
    </>
  );
});

export default ServiceListTable;
